import React, { useDeferredValue, useEffect, useState } from 'react'
import { CheckBox, CheckBoxOutlineBlank } from '@mui/icons-material'
import {
  Autocomplete as MuiAutocomplete,
  AutocompleteProps as MuiAutocompleteProps,
  Checkbox,
} from '@mui/material'
import { AutocompleteRenderOptionState } from '@mui/material/Autocomplete/Autocomplete'
import { InputProps as StandardInputProps } from '@mui/material/Input/Input'
import { InputBaseComponentProps } from '@mui/material/InputBase/InputBase'
import * as R from 'ramda'

import i18n from '../../locales/i18n'
import { Field, IdObject } from '../../types'
import { matchSubstring } from '../../utils'
import { getTranslatedFieldName } from '../../utils/languageUtils'
import PuiTextField from './PuiTextField'

interface AutocompleteProps
  extends MuiAutocompleteProps<IdObject, any, any, any> {}

export interface PuiAutocompleteProps
  extends Omit<AutocompleteProps, 'renderInput'> {
  readonly InputProps?: Partial<StandardInputProps>
  readonly field: Field
  readonly inputProps?: InputBaseComponentProps
  readonly label?: React.ReactNode
  readonly margin?: 'dense' | 'normal' | 'none'
  readonly maxSuggestions?: number
  readonly minSearchLength?: number
  readonly onValueChange?: (value: any) => void
  readonly renderInput?: AutocompleteProps['renderInput']
  readonly translateOptions?: boolean
}

const PuiAutocomplete = ({
  options: optionsProp,
  disabled,
  field,
  className,
  label,
  freeSolo = false,
  multiple = false,
  maxSuggestions = Infinity,
  minSearchLength = 0,
  InputProps,
  inputProps,
  margin,
  renderInput,
  translateOptions = false,
  ...rest
}: PuiAutocompleteProps) => {
  const options = optionsProp || []
  const textInputField: Field = {
    ...field,
    set: R.F,
    setValue: R.F,
  }

  const getInitialValue = () => {
    if (multiple) {
      return (field.value || [])
        .map((id: string) => options.find(item => item.id === id))
        .filter(Boolean)
    }
    return field.value
  }

  const [selectedItem, setSelectedItem] = useState<IdObject[] | string>(
    getInitialValue(),
  )
  const [rawInputValue, setRawInputValue] = useState('')

  const inputValue = useDeferredValue(rawInputValue)

  const isInOptions = (id: string) => options.some(R.propEq('id', id))
  const getItemId = (item: string | IdObject) =>
    R.is(String, item) ? item : item.id

  useEffect(() => {
    if (!options || !selectedItem) {
      return
    }
    if (multiple) {
      const multipleSelectedItems = selectedItem as IdObject[]
      const newSelectedItems = multipleSelectedItems.filter((item: IdObject) =>
        isInOptions(getItemId(item)),
      )
      if (
        R.symmetricDifference(newSelectedItems, multipleSelectedItems).length >
        0
      ) {
        setSelectedItem(newSelectedItems)
      }
    } else if (!isInOptions(getItemId(selectedItem as string))) {
      setSelectedItem('')
    }
  }, [options])

  useEffect(() => {
    if (freeSolo && field.value !== inputValue) {
      setRawInputValue(field.value)
    }
  }, [field.value])

  useEffect(() => {
    if (!multiple) {
      field.setValue(rawInputValue)
    }
  }, [rawInputValue])

  useEffect(() => {
    if (!multiple) {
      return
    }
    field.set(R.pluck('id', selectedItem as IdObject[]))
  }, [selectedItem])

  const getSuggestions = (maxCount: number) => {
    const inputLength = inputValue?.length ?? 0
    return inputLength >= minSearchLength
      ? (inputValue
        ? options.filter(option =>
          matchSubstring(
            inputValue,
            (translateOptions
              ? getTranslatedFieldName(option)
              : option.name) || '',
          ),
        )
        : options
      ).slice(0, maxCount)
      : []
  }

  const icon = <CheckBoxOutlineBlank />
  const checkedIcon = <CheckBox />

  const getRenderOption = (
    props: React.HTMLAttributes<HTMLLIElement>,
    option: IdObject,
    { selected }: AutocompleteRenderOptionState,
  ) => (
    <li {...props}>
      {multiple ? (
        <Checkbox checked={selected} checkedIcon={checkedIcon} icon={icon} />
      ) : null}
      {translateOptions ? getTranslatedFieldName(option) : option.name}
    </li>
  )

  const getOptionLabel = (option: string | IdObject) =>
    R.is(String, option)
      ? option
      : (translateOptions ? getTranslatedFieldName(option) : option.name) || ''

  const getOptionText = () =>
    rawInputValue
      ? i18n.t('Common:INPUTS.NO_OPTIONS')
      : i18n.t('Common:INPUTS.ENTER_SEARCH_QUERY')

  return (
    <MuiAutocomplete
      disabled={disabled}
      forcePopupIcon={false}
      freeSolo={freeSolo}
      getOptionLabel={getOptionLabel}
      inputValue={rawInputValue}
      multiple={multiple}
      noOptionsText={getOptionText()}
      options={getSuggestions(maxSuggestions)}
      renderInput={
        renderInput ||
        (params => (
          <PuiTextField
            InputProps={{
              ...params.InputProps,
              ...InputProps,
              inputProps: {
                ...params.inputProps,
                ...inputProps,
              },
            }}
            className={className}
            field={textInputField}
            label={label}
            margin={margin}
            variant="standard"
            {...R.omit(['InputProps', 'inputProps'], params)}
          />
        ))
      }
      renderOption={getRenderOption}
      value={selectedItem}
      // @ts-ignore
      onChange={(event, newValue) => setSelectedItem(newValue)}
      onInputChange={(event, newValue) => setRawInputValue(newValue)}
      {...rest}
    />
  )
}

export default PuiAutocomplete
